
import React, { useEffect, useState } from 'react';
import AdminSidebar from "../../components/layout/AdminSidebar";

export default function AdminUserSettingsPage() {
    const [email, setEmail] = useState('');
    const [user, setUser] = useState(null);

    const [role, setRole] = useState('');
    const [areas, setAreas] = useState([]);
    const [selectedAreaIds, setSelectedAreaIds] = useState([]);

    const [loadingUser, setLoadingUser] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    /*
     * Change these URLs to match your backend.
     */
    const API_URL = process.env.REACT_APP_API_URL;

    // --------------------------------------------------
    // Search for user
    // --------------------------------------------------

    const findUser = async () => {
        if (!email.trim()) {
            setError('Ange en e-postadress.');
            return;
        }

        setError('');
        setSuccess('');
        setUser(null);
        setSelectedAreaIds([]);

        try {
            setLoadingUser(true);
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_URL}/api/users/admin?email=${encodeURIComponent(email.trim())}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Ingen användare hittades med den e-postadressen.');
                }

                throw new Error('Kunde inte hämta användaren.');
            }

            const data = await response.json();

            setUser(data.user);
            setRole(data.user.role);

            // If user is already a LOCALADMIN,
            // populate their existing area access.
            if (data.user.role === 'LOCALADMIN') {
                setSelectedAreaIds(
                    (data.user.areas || []).map(area => area.id)
                );
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingUser(false);
        }
    };

    // --------------------------------------------------
    // Fetch all areas
    // --------------------------------------------------

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                setLoadingAreas(true);

                const response = await fetch(
                    `${API_URL}/api/areas`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Kunde inte hämta naturområden.');
                }

                const data = await response.json();

                setAreas(data.areas || data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingAreas(false);
            }
        };

        fetchAreas();
    }, [API_URL]);

    // --------------------------------------------------
    // Handle role change
    // --------------------------------------------------

    const handleRoleChange = (event) => {
        const newRole = event.target.value;

        setRole(newRole);
        setSuccess('');
        setError('');

        // If they are changed away from LOCALADMIN,
        // clear their selected areas.
        if (newRole !== 'LOCALADMIN') {
            setSelectedAreaIds([]);
        }
    };

    // --------------------------------------------------
    // Handle area checkbox
    // --------------------------------------------------

    const handleAreaToggle = (areaId) => {
        setSelectedAreaIds(prev => {
            if (prev.includes(areaId)) {
                return prev.filter(id => id !== areaId);
            }

            return [...prev, areaId];
        });
    };

    // --------------------------------------------------
    // Select all areas
    // --------------------------------------------------

    const selectAllAreas = () => {
        setSelectedAreaIds(areas.map(area => area.id));
    };

    // --------------------------------------------------
    // Deselect all areas
    // --------------------------------------------------

    const deselectAllAreas = () => {
        setSelectedAreaIds([]);
    };

    // --------------------------------------------------
    // Save
    // --------------------------------------------------

    const handleSave = async () => {
        if (!user) {
            return;
        }

        setError('');
        setSuccess('');

        try {
            setSaving(true);
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_URL}/api/users/admin/${user.id}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        role: role,
                        area_ids: role === 'LOCALADMIN'
                            ? selectedAreaIds
                            : [],
                    }),
                }
            );

            if (!response.ok) {
                const data = await response.json().catch(() => null);

                throw new Error(
                    data?.message || 'Kunde inte uppdatera användaren.'
                );
            }

            setSuccess('Användaren har uppdaterats.');

            // Update local representation too
            setUser(prev => ({
                ...prev,
                role: role,
            }));

        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 py-10">
            <AdminSidebar />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-charcoal">
                        Hantera användare
                    </h1>

                    <p className="mt-2 text-gray-600">
                        Sök efter en användare och ändra deras behörighet.
                    </p>
                </div>

                {/* Main card */}
                <div className="bg-white rounded-xl shadow-lg p-6">

                    {/* Email search */}
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Användarens e-postadress
                        </label>

                        <div className="flex gap-3">
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        findUser();
                                    }
                                }}
                                placeholder="namn@example.com"
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg
                                           focus:outline-none focus:ring-2 focus:ring-sage
                                           focus:border-transparent"
                            />

                            <button
                                type="button"
                                onClick={findUser}
                                disabled={loadingUser}
                                className="px-6 py-3 bg-charcoal text-white rounded-lg
                                           font-medium hover:bg-gray-800
                                           disabled:opacity-50 disabled:cursor-not-allowed
                                           transition-colors"
                            >
                                {loadingUser ? 'Söker...' : 'Sök'}
                            </button>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200
                                        text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Success */}
                    {success && (
                        <div className="mt-6 p-4 bg-green-50 border border-green-200
                                        text-green-700 rounded-lg">
                            {success}
                        </div>
                    )}

                    {/* User information */}
                    {user && (
                        <div className="mt-8">

                            {/* User info */}
                            <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                                <h2 className="text-lg font-semibold text-charcoal mb-4">
                                    Användare
                                </h2>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500">
                                            Namn
                                        </p>

                                        <p className="font-medium text-gray-800">
                                            {user.firstname} {user.lastname}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-500">
                                            E-post
                                        </p>

                                        <p className="font-medium text-gray-800">
                                            {user.email}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Role */}
                            <div className="mt-6">
                                <label
                                    htmlFor="role"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Behörighet
                                </label>

                                <select
                                    id="role"
                                    value={role}
                                    onChange={handleRoleChange}
                                    className="w-full px-4 py-3 border border-gray-300
                                               rounded-lg bg-white
                                               focus:outline-none focus:ring-2
                                               focus:ring-sage focus:border-transparent"
                                >
                                    <option value="USER">
                                        USER
                                    </option>

                                    <option value="LOCALADMIN">
                                        LOCALADMIN
                                    </option>

                                    <option value="ADMIN">
                                        ADMIN
                                    </option>
                                </select>
                            </div>

                            {/* Areas */}
                            <div
                                className={`mt-6 transition-opacity ${
                                    role === 'LOCALADMIN'
                                        ? 'opacity-100'
                                        : 'opacity-50'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h2 className="text-lg font-semibold text-charcoal">
                                            Naturområden
                                        </h2>

                                        <p className="text-sm text-gray-500">
                                            Välj vilka områden denna LOCALADMIN
                                            ska kunna administrera.
                                        </p>
                                    </div>

                                    {role === 'LOCALADMIN' && (
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={selectAllAreas}
                                                className="text-sm px-3 py-2
                                                           text-charcoal hover:bg-gray-100
                                                           rounded-lg"
                                            >
                                                Välj alla
                                            </button>

                                            <button
                                                type="button"
                                                onClick={deselectAllAreas}
                                                className="text-sm px-3 py-2
                                                           text-gray-600 hover:bg-gray-100
                                                           rounded-lg"
                                            >
                                                Avmarkera alla
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Scrollable checklist */}
                                <div
                                    className={`border border-gray-300 rounded-lg
                                                overflow-y-auto max-h-80
                                                bg-white ${
                                                    role !== 'LOCALADMIN'
                                                        ? 'pointer-events-none'
                                                        : ''
                                                }`}
                                >
                                    {loadingAreas ? (
                                        <div className="p-6 text-center text-gray-500">
                                            Laddar områden...
                                        </div>
                                    ) : areas.length === 0 ? (
                                        <div className="p-6 text-center text-gray-500">
                                            Inga områden hittades.
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {areas.map(area => {
                                                const isChecked =
                                                    selectedAreaIds.includes(area.id);

                                                return (
                                                    <label
                                                        key={area.id}
                                                        className={`flex items-center px-4 py-3
                                                                    transition-colors ${
                                                                        role === 'LOCALADMIN'
                                                                            ? 'cursor-pointer hover:bg-gray-50'
                                                                            : 'cursor-not-allowed'
                                                                    }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            disabled={role !== 'LOCALADMIN'}
                                                            onChange={() =>
                                                                handleAreaToggle(area.id)
                                                            }
                                                            className="w-4 h-4 rounded
                                                                       border-gray-300
                                                                       text-sage
                                                                       focus:ring-sage"
                                                        />

                                                        <span className="ml-3 text-gray-800">
                                                            {area.name}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Selected count */}
                                {role === 'LOCALADMIN' && (
                                    <p className="mt-2 text-sm text-gray-500">
                                        {selectedAreaIds.length} område(n) valda
                                    </p>
                                )}
                            </div>

                            {/* Save */}
                            <div className="mt-8 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-8 py-3 bg-forest text-white
                                               rounded-lg font-medium
                                               hover:bg-leaf
                                               disabled:opacity-50
                                               disabled:cursor-not-allowed
                                               transition-colors"
                                >
                                    {saving
                                        ? 'Sparar...'
                                        : 'Spara ändringar'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

